import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Sparkles, Download, Check, Trash2, Image as ImageIcon, Copy, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import AdminLayout from '@/components/admin/AdminLayout';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin Meme Generator
 *
 * Workflow:
 *   1. Pull trending AI topics from HN + Reddit (or type one in)
 *   2. Pick a topic → click "Generate" → Claude writes a niche-tech caption,
 *      Nano Banana renders the image, Pillow overlays the text
 *   3. Preview, download PNG for IG/X, or trash if it doesn't land
 *
 * Soft-capped at 6 generations per calendar month. Counter is visible in the
 * header so the admin can see what's left without doing math.
 */
export default function AdminMemes() {
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [memes, setMemes] = useState([]);
  const [usedThisMonth, setUsedThisMonth] = useState(0);
  const [monthlyCap, setMonthlyCap] = useState(6);

  const [topic, setTopic] = useState('');
  const [audience, setAudience] = useState('AI creators');
  const [tone, setTone] = useState('dry');
  const [format, setFormat] = useState('ig_portrait');
  const [cta, setCta] = useState('auto');
  const [ctaBanners, setCtaBanners] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [latestMeme, setLatestMeme] = useState(null);
  const [previewRecomposing, setPreviewRecomposing] = useState(false);

  const authHeaders = () => ({ withCredentials: true });

  const loadLibrary = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/memes`, authHeaders());
      setMemes(res.data.memes || []);
      setUsedThisMonth(res.data.used_this_month || 0);
      setMonthlyCap(res.data.monthly_cap || 6);
    } catch (e) {
      toast.error('Failed to load meme library');
    }
  };

  const pullTopics = async () => {
    setTopicsLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/memes/trending-topics`, authHeaders());
      setTopics(res.data.topics || []);
      toast.success(`Pulled ${res.data.topics?.length || 0} trending topics`);
    } catch (e) {
      toast.error('Failed to pull trending topics');
    } finally {
      setTopicsLoading(false);
    }
  };

  useEffect(() => { loadLibrary(); }, []);

  // Load CTA banner registry once. Small static payload — fine to fetch
  // on mount without caching.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/admin/memes/cta-banners`, authHeaders());
        setCtaBanners(res.data.banners || []);
      } catch { /* silent — CTA picker just shows the default */ }
    })();
  }, []);

  const selectedCtaPreview = ctaBanners.find((b) => b.slug === cta)?.preview_url;

  const generate = async () => {
    if (!topic.trim()) { toast.error('Pick or type a topic first'); return; }
    if (usedThisMonth >= monthlyCap) {
      toast.error(`Monthly cap reached (${usedThisMonth}/${monthlyCap}). Archive an older meme to free a slot.`);
      return;
    }
    setGenerating(true);
    setLatestMeme(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/memes/generate`,
        { topic, audience, tone, format, cta },
        authHeaders(),
      );
      setLatestMeme(res.data);
      toast.success('Meme generated — review the preview below');
      await loadLibrary();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Generation failed';
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  const approveMeme = async (memeId) => {
    try {
      await axios.post(`${API}/api/admin/memes/${memeId}/approve`, {}, authHeaders());
      toast.success('Approved — saved to library');
      await loadLibrary();
    } catch {
      toast.error('Approve failed');
    }
  };

  const trashMeme = async (memeId) => {
    if (!window.confirm('Archive this meme? (Frees up a monthly slot.)')) return;
    try {
      await axios.delete(`${API}/api/admin/memes/${memeId}`, authHeaders());
      toast.success('Archived');
      if (latestMeme?.meme_id === memeId) setLatestMeme(null);
      await loadLibrary();
    } catch {
      toast.error('Archive failed');
    }
  };

  const downloadMeme = (memeId) => {
    window.open(`${API}/api/admin/memes/${memeId}/download`, '_blank');
  };

  const copyCaption = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Caption copied');
  };

  const remaining = Math.max(0, monthlyCap - usedThisMonth);

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-memes-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black flex items-center gap-3">
              <Sparkles className="w-7 h-7 text-[#00F0FF]" />
              Viral Meme Generator
            </h1>
            <p className="text-neutral-400 mt-1">Niche-tech voice. Claude writes the caption, Nano Banana renders the image.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="px-4 py-2 bg-neutral-900/60 border border-white/10 rounded-lg text-sm" data-testid="memes-usage">
              <span className="text-neutral-400">This month: </span>
              <span className={`font-bold ${remaining === 0 ? 'text-red-400' : remaining <= 2 ? 'text-yellow-400' : 'text-[#00FF94]'}`}>
                {usedThisMonth}/{monthlyCap}
              </span>
            </div>
          </div>
        </div>

        {/* Topic picker */}
        <div className="bg-neutral-900/40 border border-white/10 rounded-xl p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-bold text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-400" />
              Topic source
            </h2>
            <Button
              onClick={pullTopics}
              disabled={topicsLoading}
              variant="outline"
              size="sm"
              className="border-white/20"
              data-testid="memes-pull-topics-btn"
            >
              {topicsLoading
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <RefreshCw className="w-4 h-4 mr-2" />}
              Pull trending (HN + Reddit AI)
            </Button>
          </div>

          {topics.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto pr-2" data-testid="memes-topic-list">
              {topics.map((t, idx) => (
                <button
                  key={idx}
                  onClick={() => setTopic(t.title)}
                  className={`text-left px-3 py-2 rounded-lg border transition text-sm ${
                    topic === t.title
                      ? 'border-[#00F0FF] bg-[#00F0FF]/10 text-white'
                      : 'border-white/10 bg-neutral-900/60 hover:border-white/30 text-neutral-200'
                  }`}
                  data-testid={`memes-topic-${idx}`}
                >
                  <div className="font-medium leading-snug line-clamp-2">{t.title}</div>
                  <div className="text-[11px] text-neutral-500 mt-1">{t.source} · {t.score} pts</div>
                </button>
              ))}
            </div>
          )}

          <div>
            <label className="text-xs uppercase tracking-wide text-neutral-400 font-bold">Topic (edit or type your own)</label>
            <Textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. AI agents can now book your flights, your dentist, and your divorce"
              className="mt-1 bg-neutral-900/60 border-white/10 text-white"
              rows={2}
              data-testid="memes-topic-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wide text-neutral-400 font-bold">Audience</label>
              <Input value={audience} onChange={(e) => setAudience(e.target.value)} className="mt-1 bg-neutral-900/60 border-white/10 text-white" data-testid="memes-audience-input" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-neutral-400 font-bold">Tone</label>
              <select value={tone} onChange={(e) => setTone(e.target.value)} className="mt-1 w-full bg-neutral-900/60 border border-white/10 text-white rounded-md px-3 py-2" data-testid="memes-tone-select">
                <option value="dry">Dry</option>
                <option value="absurdist">Absurdist</option>
                <option value="sincere-but-funny">Sincere but funny</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-neutral-400 font-bold">Format</label>
              <select value={format} onChange={(e) => setFormat(e.target.value)} className="mt-1 w-full bg-neutral-900/60 border border-white/10 text-white rounded-md px-3 py-2" data-testid="memes-format-select">
                <option value="ig_portrait">Instagram portrait (1080×1350)</option>
                <option value="square">Square (1080×1080)</option>
              </select>
            </div>
          </div>

          {/* CTA banner picker — rotate per generation. Preview thumbnail
              renders below so the admin sees exactly what will be burned
              into the bottom of the meme before clicking Generate. */}
          <div>
            <label className="text-xs uppercase tracking-wide text-neutral-400 font-bold">CTA banner</label>
            <select
              value={cta}
              onChange={(e) => setCta(e.target.value)}
              className="mt-1 w-full bg-neutral-900/60 border border-white/10 text-white rounded-md px-3 py-2"
              data-testid="memes-cta-select"
            >
              {ctaBanners.length === 0 && <option value="meme_is_real">The meme is real — see the tools</option>}
              {ctaBanners.map((b) => (
                <option key={b.slug} value={b.slug}>{b.label}</option>
              ))}
            </select>
            {selectedCtaPreview && (
              <div className="mt-2 rounded-md overflow-hidden border border-white/10" data-testid="memes-cta-preview">
                <img src={selectedCtaPreview} alt="CTA banner preview" className="block w-full" />
              </div>
            )}
          </div>

          <Button
            onClick={generate}
            disabled={generating || !topic.trim() || remaining === 0}
            className="bg-[#00F0FF] text-black font-bold hover:bg-[#00F0FF]/90 disabled:opacity-50"
            data-testid="memes-generate-btn"
          >
            {generating
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating (~25s)…</>
              : <><Sparkles className="w-4 h-4 mr-2" /> Generate meme</>}
          </Button>
        </div>

        {/* Preview of just-generated meme */}
        {latestMeme && (
          <div className="bg-neutral-900/40 border border-[#00F0FF]/30 rounded-xl p-5" data-testid="memes-preview">
            <div className="flex flex-wrap items-start gap-6">
              <img
                src={`data:image/png;base64,${latestMeme.composited_b64}`}
                alt={latestMeme.headline}
                className="rounded-lg max-w-full md:max-w-md border border-white/10"
              />
              <div className="flex-1 min-w-[280px] space-y-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-neutral-400 font-bold mb-1">Headline · pattern {latestMeme.pattern || '?'}</div>
                  <div className="text-lg font-semibold text-white">{latestMeme.headline}</div>
                </div>
                {latestMeme.cta && (
                  <div className="space-y-2">
                    <div className="text-xs text-neutral-400">
                      CTA used: <span className="text-white font-semibold">{latestMeme.cta.replace(/_/g,' ')}</span>
                      {latestMeme.cta_suggested && latestMeme.cta_suggested === latestMeme.cta && (
                        <span className="ml-2 px-2 py-0.5 rounded-full bg-[#00F0FF]/15 text-[#00F0FF] text-[10px] font-bold uppercase tracking-wider">AI suggested</span>
                      )}
                    </div>
                    {/* Swap CTA on the just-generated meme — re-render with
                        a different banner without burning quota. ~200ms. */}
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Swap CTA (no quota used)</label>
                      <div className="flex items-center gap-2 mt-1">
                        <select
                          value={latestMeme.cta}
                          disabled={previewRecomposing}
                          onChange={async (e) => {
                            const newCta = e.target.value;
                            if (newCta === latestMeme.cta) return;
                            setPreviewRecomposing(true);
                            try {
                              const res = await axios.post(
                                `${API}/api/admin/memes/${latestMeme.meme_id}/recompose`,
                                { cta: newCta },
                                authHeaders(),
                              );
                              setLatestMeme(res.data);
                              toast.success('CTA swapped — no quota used');
                              await loadLibrary();
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || 'CTA swap failed');
                            } finally {
                              setPreviewRecomposing(false);
                            }
                          }}
                          className="flex-1 bg-neutral-900/60 border border-white/10 text-white text-xs rounded-md px-2 py-1.5"
                          data-testid="memes-preview-cta-select"
                        >
                          {ctaBanners.filter((b) => b.slug !== 'auto').map((b) => (
                            <option key={b.slug} value={b.slug}>{b.label}</option>
                          ))}
                        </select>
                        {previewRecomposing && <Loader2 className="w-4 h-4 animate-spin text-[#00F0FF]" />}
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-xs uppercase tracking-wide text-neutral-400 font-bold mb-1">Post caption</div>
                  <div className="text-sm text-neutral-200 leading-relaxed">{latestMeme.post_caption}</div>
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  <Button onClick={() => downloadMeme(latestMeme.meme_id)} size="sm" className="bg-white text-black hover:bg-white/90" data-testid="memes-preview-download">
                    <Download className="w-4 h-4 mr-2" /> Download PNG
                  </Button>
                  <Button onClick={() => copyCaption(latestMeme.post_caption || '')} size="sm" variant="outline" className="border-white/20" data-testid="memes-preview-copy-caption">
                    <Copy className="w-4 h-4 mr-2" /> Copy caption
                  </Button>
                  <Button onClick={() => approveMeme(latestMeme.meme_id)} size="sm" variant="outline" className="border-[#00FF94]/40 text-[#00FF94] hover:bg-[#00FF94]/10" data-testid="memes-preview-approve">
                    <Check className="w-4 h-4 mr-2" /> Approve
                  </Button>
                  <Button onClick={() => trashMeme(latestMeme.meme_id)} size="sm" variant="outline" className="border-red-400/40 text-red-400 hover:bg-red-400/10" data-testid="memes-preview-trash">
                    <Trash2 className="w-4 h-4 mr-2" /> Trash
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Library */}
        <div>
          <h2 className="font-bold text-lg mb-3 flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-neutral-300" />
            Library
            <span className="text-sm font-normal text-neutral-500">({memes.length})</span>
          </h2>
          {memes.length === 0 ? (
            <div className="text-sm text-neutral-500 italic">No memes yet. Generate one above.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="memes-library">
              {memes.map((m) => (
                <LibraryCard
                  key={m.meme_id}
                  meme={m}
                  ctaBanners={ctaBanners}
                  onChanged={loadLibrary}
                  onDownload={() => downloadMeme(m.meme_id)}
                  onApprove={() => approveMeme(m.meme_id)}
                  onTrash={() => trashMeme(m.meme_id)}
                  onCopy={() => copyCaption(m.post_caption || '')}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function LibraryCard({ meme, ctaBanners, onChanged, onDownload, onApprove, onTrash, onCopy }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recomposing, setRecomposing] = useState(false);

  // Lazy-load the base64 preview only when the admin clicks "Show" — keeps
  // the library list payload tiny even if there are dozens of past memes.
  const showPreview = async () => {
    if (preview) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/memes/${meme.meme_id}`, { withCredentials: true });
      setPreview(res.data.composited_b64);
    } catch {
      toast.error('Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  // Swap the CTA on an EXISTING meme — no LLM cost, no quota burn. The
  // backend reuses the stored base image + headline and just re-pastes
  // a different bottom banner. ~200ms.
  const swapCta = async (newCta) => {
    if (newCta === meme.cta) return;
    setRecomposing(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/memes/${meme.meme_id}/recompose`,
        { cta: newCta },
        { withCredentials: true },
      );
      setPreview(res.data.composited_b64);
      toast.success('CTA swapped — no quota used');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'CTA swap failed');
    } finally {
      setRecomposing(false);
    }
  };

  const badge = meme.status === 'approved'
    ? 'bg-[#00FF94]/20 text-[#00FF94]'
    : meme.status === 'archived'
      ? 'bg-neutral-700 text-neutral-400'
      : 'bg-yellow-500/20 text-yellow-400';

  return (
    <div className="bg-neutral-900/40 border border-white/10 rounded-lg p-4 space-y-3" data-testid={`memes-library-card-${meme.meme_id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${badge}`}>{meme.status}</span>
        <span className="text-[10px] text-neutral-500">{(meme.created_at || '').slice(0, 10)}</span>
      </div>
      <div className="text-sm font-semibold text-white line-clamp-3 leading-snug min-h-[3.5rem]">{meme.headline}</div>
      {preview ? (
        <img src={`data:image/png;base64,${preview}`} alt={meme.headline} className="rounded w-full border border-white/10" />
      ) : (
        <Button onClick={showPreview} disabled={loading} size="sm" variant="outline" className="w-full border-white/10 text-neutral-300">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Show preview'}
        </Button>
      )}
      {/* CTA swap — re-render the bottom banner without burning quota */}
      {ctaBanners && ctaBanners.length > 0 && (
        <div>
          <label className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Swap CTA (no quota used)</label>
          <div className="flex items-center gap-2 mt-1">
            <select
              value={meme.cta || 'meme_is_real'}
              onChange={(e) => swapCta(e.target.value)}
              disabled={recomposing}
              className="flex-1 bg-neutral-900/60 border border-white/10 text-white text-xs rounded-md px-2 py-1.5"
              data-testid={`memes-library-cta-select-${meme.meme_id}`}
            >
              {ctaBanners.filter((b) => b.slug !== 'auto').map((b) => (
                <option key={b.slug} value={b.slug}>{b.label}</option>
              ))}
            </select>
            {recomposing && <Loader2 className="w-4 h-4 animate-spin text-[#00F0FF]" />}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 pt-1">
        <Button onClick={onDownload} size="sm" variant="outline" className="border-white/20 text-xs">
          <Download className="w-3 h-3 mr-1" /> PNG
        </Button>
        <Button onClick={onCopy} size="sm" variant="outline" className="border-white/20 text-xs">
          <Copy className="w-3 h-3 mr-1" /> Caption
        </Button>
        {meme.status === 'draft' && (
          <Button onClick={onApprove} size="sm" variant="outline" className="border-[#00FF94]/40 text-[#00FF94] text-xs">
            <Check className="w-3 h-3 mr-1" /> Approve
          </Button>
        )}
        {meme.status !== 'archived' && (
          <Button onClick={onTrash} size="sm" variant="outline" className="border-red-400/40 text-red-400 text-xs">
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
      </div>
    </div>
  );
}
